import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthenService } from '@core/authen/authen.service';
import { BaseHttpService } from '@core/http/base-http.service';
import { ChallengeHttpService } from '@core/http/challenge-http.service';
import { OrganizationHttpService } from '@core/http/organization-http.service';
import { CentralConfigService } from '@core/services/central-config.service';
import { CommunitySelectorStoredService } from '@core/services/community-selector-stored.service';
import { FactoryService } from '@core/services/factory.service';
import { GlobalFilterStoredService } from '@core/services/global-filter-stored.service';
import { SearchChannelsService } from '@core/services/search-channels.service';
import { UserConfigManagementService } from '@core/services/user-config-management.service';
import { SessionService } from '@core/session.service';
import { environment } from '@env/environment';
import {
  NgbDropdown,
  NgbDropdownModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  matCloseOutline,
  matSearchOutline,
} from '@ng-icons/material-icons/outline';
import { TranslateModule } from '@ngx-translate/core';
import { InternalIconComponent } from '@shared/components/internal-icon/internal-icon.component';
import { OrganizationLogoComponent } from '@shared/components/molecules/organization-logo/organization-logo.component';
import { ToBoolean } from '@shared/decorators/to-boolean';
import { DebounceClickDirective } from '@shared/directives/debounce-click.directive';
import { DefaultPlaceholderDirective } from '@shared/directives/default-placeholder.directive';
import { DynamicImgUrlDirective } from '@shared/directives/dynamic-img-url.directive';
import { UnsavedFormCheckDirective } from '@shared/directives/unsaved-form-check.directive';
import { EntityName } from '@shared/enums/entity-name.enum';
import { GlobalEvent } from '@shared/enums/event-bus.enum';
import { HttpStatusCodeEnum } from '@shared/enums/httpstatuscode.enum';
import { IconType, InternalIcon } from '@shared/enums/internal-icon.enum';
import { StorageEnum } from '@shared/enums/storage.enum';
import { UrlParam } from '@shared/enums/url-param.enum';
import { untilDestroyed } from '@shared/functions/until-destroyed';
import { WINDOW } from '@shared/helpers/window.token';
import { GlobalFilterCriteria } from '@shared/interfaces/filters/global-filter.interface';
import {
  OrganizationFilter,
  OrganizationInterface,
} from '@shared/interfaces/organization.interface';
import { Entity } from '@shared/models/entity.model';
import { CallPipe } from '@shared/pipes/call.pipe';
import { FormUtils } from '@shared/utils/form-utils';
import { StringUtils } from '@shared/utils/string-utils';
import { isMobile } from 'detect-touch-device';
import { EventBusService } from 'ngx-eventbus';
import { Event as EventBusEvent } from 'ngx-eventbus/lib/service/event';
import {
  BehaviorSubject,
  lastValueFrom,
  Observable,
  of,
  throwError,
} from 'rxjs';
import { catchError, filter } from 'rxjs/operators';

@Component({
  selector: 'app-global-filter',
  templateUrl: './global-filter.component.html',
  imports: [
    NgIcon,
    CommonModule,
    UnsavedFormCheckDirective,
    DefaultPlaceholderDirective,
    DynamicImgUrlDirective,
    FormsModule,
    DebounceClickDirective,
    NgbDropdownModule,
    TranslateModule,
    NgbTooltipModule,
    OrganizationLogoComponent,
    InternalIconComponent,
    CallPipe,
  ],
  viewProviders: [provideIcons({ matCloseOutline, matSearchOutline })],
})
export class GlobalFilterComponent implements OnInit, OnDestroy {
  @Input() isMobileFilterMode = false;

  @Output() filterPanelClosed = new EventEmitter();

  @Input() @ToBoolean() standalone_widget_mode = false;

  @Input() @ToBoolean() hide_innovation_space = false;

  @Input() showSearchBar: boolean;

  @Input() showActiveInnovationSpaceImg: boolean;

  @Input() isShowSearch = false;

  dropdown: NgbDropdown;

  @ViewChildren(NgbDropdown)
  dropdowns: QueryList<NgbDropdown>;
  //#region HANDLE FILTER PANE
  dropDownPlacement = 'bottom-left';

  globalPaneEvent: EventBusEvent;

  authenticated$: Observable<boolean>;

  organizationId: number = null;
  organizationSlug: string;

  orgFilterList: OrganizationFilter[] = [];
  orgUrl = environment.jipUrl.organizations;

  searchKeyword = '';

  threadId: number = null;
  threadEntity = '';

  InternalIcon = InternalIcon;
  IconType = IconType;

  hasTouchInput: boolean = isMobile;
  dropDownOffsetX = '';

  centralConfig: CentralConfigService;
  protected filterStoredService: GlobalFilterStoredService;

  allInnovation = !this.organizationId;

  popularOrgs: (OrganizationFilter & OrganizationInterface)[] = [];

  orgFilterList$ = new BehaviorSubject<OrganizationFilter[]>([]);

  organizationHttpService = inject(OrganizationHttpService);

  get hasFilters(): boolean {
    return !!this.organizationId;
  }

  get selectedOrg(): OrganizationFilter {
    if (this.organizationId) {
      const org = this.orgFilterList?.find(x => x.id === this.organizationId);

      if (org) {
        this.orgUrl = org.isChallenge
          ? environment.jipUrl.challenges
          : environment.jipUrl.organizations;
      }

      return org;
    }

    return null;
  }

  get tenantLogo(): string {
    return this.sessionService.getTenantLogo();
  }

  get windowOrigin(): string {
    return globalThis.origin;
  }

  get noFilterApplied(): boolean {
    return !this.organizationId;
  }

  @ViewChild('dropdownRef', { static: false }) set content(
    content: ElementRef
  ) {
    if (content?.nativeElement) {
      const ele = document.getElementById('filterCollapse')?.parentElement;

      const dropDownOffsetX =
        ele?.getBoundingClientRect().right - window.innerWidth / 2;

      this.dropDownOffsetX = `translateX(-${Math.round(dropDownOffsetX)}px)`;
    }
  }

  orgService: BaseHttpService<Entity> = null;

  constructor(
    @Inject(WINDOW) protected window: Window,
    public sessionService: SessionService,
    protected eventBus: EventBusService,
    protected authService: AuthenService,
    protected factoryService: FactoryService,
    protected userConfig: UserConfigManagementService,
    protected readonly renderer: Renderer2,
    protected readonly injector: Injector,
    protected searchChannel: SearchChannelsService,
    protected orgHttpService: OrganizationHttpService,
    protected communitySelectorStored: CommunitySelectorStoredService
  ) {
    this.filterStoredService = this.injector.get<GlobalFilterStoredService>(
      GlobalFilterStoredService
    );

    this.centralConfig = this.injector.get(CentralConfigService);

    this.initOrgService();
    this.sessionService.isHeaderVisible$.next(true);
  }

  ngOnInit(): void {
    this.listenFilterPaneEvent();

    this.authenticated$ = this.authService.authenticated();

    this.initializeFilters().then(() => {
      this.filterStoredService.filterReady$.next(true);
    });
  }

  protected async initializeFilters(): Promise<void> {
    const persistedFilters = await this.filterStoredService.getFilterCriteria();
    const isInitSearchAll = this.isInitSearchAll();
    const shouldKeepSearchKeyword =
      location.pathname === persistedFilters.filteringPath || isInitSearchAll;

    this.threadId = persistedFilters.threadId;
    this.threadEntity = persistedFilters.threadEntity;

    // The active space is only restored when the url carries the
    // organizationId param: in-app navigation always propagates it, so a
    // param-less page load came from outside the app and resets the space.
    if (this.centralConfig.innovationSpaceId) {
      this.organizationId = this.centralConfig.innovationSpaceId;
    } else if (StringUtils.getParamFromUrl(UrlParam.FilterTokenId)) {
      this.organizationId = persistedFilters.organizationIds?.[0] ?? null;
    } else {
      this.organizationId = null;
      await this.filterStoredService.resetInnovationSpace();
    }

    // If reload the same page => keep display search keyword in search box
    this.searchKeyword = shouldKeepSearchKeyword
      ? (persistedFilters.keyword ?? '')
      : this.searchKeyword;

    if (isInitSearchAll) await this.saveFilterCriteria();

    await this.handleFilterTokenFromUrl();

    this.listenAddOrgToTokenEvent();

    this.filterStoredService.setCurrentFilterToken(this.organizationId);

    this.handleListenToTopicFilterChange();

    this.communitySelectorStored.setSelectedOrg(this.selectedOrg);

    if (shouldKeepSearchKeyword) {
      this.communitySelectorStored.setKeyword(persistedFilters.keyword ?? '');
    }
  }

  isInitSearchAll(): boolean {
    const { searchResults } = environment.jipUrl;
    const isNewSearch = !!localStorage.getItem(StorageEnum.isNewSearch);

    localStorage.removeItem(StorageEnum.isNewSearch);

    return location.pathname.includes(searchResults) && isNewSearch;
  }

  async filterOrganizations(
    orgId: number,
    isNewFilterToken = false
  ): Promise<void> {
    if (orgId) {
      if (!isNewFilterToken) {
        this.updateOrgFilterTokenToLatest(orgId);
      }

      if (
        this.organizationId === orgId &&
        !this.centralConfig.innovationSpaceId
      ) {
        this.organizationId = null;
        StringUtils.removeParamFromUrl(UrlParam.SubOrganizationId);
      } else {
        this.organizationId = orgId;
      }
    }

    this.threadId = null;
    this.threadEntity = '';

    const filterCriteria = await this.saveFilterCriteria();

    this.communitySelectorStored.activeOrgSuccessfulSave$.next(true);
    this.afterFilterSelection(filterCriteria);
    this.communitySelectorStored.setSelectedOrg(this.selectedOrg);
  }

  async search(): Promise<void> {
    const persistedFilters = await this.filterStoredService.getFilterCriteria();
    // don't search if keyword is same as before
    if (
      persistedFilters.keyword === this.searchKeyword &&
      location.pathname === persistedFilters.filteringPath &&
      location.pathname.includes(environment.jipUrl.searchResults)
    ) {
      return;
    }

    const filterCriteria = await this.saveFilterCriteria();
    filterCriteria.keyword = this.searchKeyword;

    this.triggerGlobalFilterEvent(filterCriteria, true);
  }

  resetSearch(): void {
    this.searchKeyword = '';
    this.search();
  }

  //#endregion End Handle Filter Pane

  closeFilters(): void {
    this.isMobileFilterMode = false;
    this.filterPanelClosed.emit(false);
  }

  //#region HANDLE FILTER TOKEN
  async handleFilterTokenFromUrl(): Promise<void> {
    await this.handleCurrentFilterList();

    let organizationId: number;
    let slug: string;

    const filterTokenValue = StringUtils.getParamFromUrl(
      UrlParam.FilterTokenId
    );

    const isNumeric = !Number.isNaN(Number(filterTokenValue));

    if (isNumeric) {
      organizationId = +filterTokenValue;
    } else {
      slug = filterTokenValue;
    }

    if (this.shouldHandleTenantChange(filterTokenValue)) {
      this.organizationId = null;
      StringUtils.removeParamFromUrl(UrlParam.FilterTokenId);
      this.communitySelectorStored.setSelectedOrg(null);

      const filterCriteria = await this.saveFilterCriteria();
      this.afterFilterSelection(filterCriteria);

      return;
    }

    await this.handleSubOrganization(filterTokenValue);

    if (organizationId) {
      await this.handleFilterTokenAsId(organizationId);
    } else {
      await this.handleFilterTokenAsSlug(slug);
    }

    // Remove param if white label sites
    if (this.centralConfig.innovationSpaceId) {
      StringUtils.removeParamFromUrl(UrlParam.FilterTokenId);
    }
  }

  async handleCurrentFilterList(): Promise<void> {
    const currentOrgFilter = await this.userConfig.get(
      StorageEnum.orgFilterToken
    );

    if (currentOrgFilter) {
      this.orgFilterList = this.removeDuplicates(currentOrgFilter);
      this.orgFilterList$.next(this.orgFilterList);
    }
  }

  async handleSubOrganization(filterTokenValue: string): Promise<void> {
    if (
      (filterTokenValue || this.organizationId) &&
      (this.sessionService.isOnListPage || this.sessionService.isOnHomePage) &&
      this.sessionService.hasTopicFilter
    ) {
      await this.handleSubOrgFromUrl();
    } else {
      StringUtils.removeParamFromUrl(UrlParam.SubOrganizationId);
    }
  }

  async handleFilterTokenAsId(organizationId: number): Promise<void> {
    if (organizationId) {
      const orgId = Number(organizationId);

      if (Number.isNaN(orgId)) {
        return;
      }

      await this.updateFilterTokenStatus(orgId);
    }
  }

  async handleFilterTokenAsSlug(slug: string): Promise<void> {
    if (slug) {
      // Saved tokens (ORG_FILTER_TOKEN) already carry the slug → skip the API call.
      const storedOrgId = this.orgFilterList.find(org => org.slug === slug)?.id;

      const orgId =
        storedOrgId ??
        (await lastValueFrom(
          this.organizationHttpService.getEntityIdBySlug(slug).pipe(
            catchError(() => {
              StringUtils.removeParamFromUrl(UrlParam.FilterTokenId);
              globalThis.location.reload();

              return of(null);
            })
          )
        ));

      if (!orgId) return;

      await this.updateFilterTokenStatus(orgId);
    }
  }

  shouldHandleTenantChange(filterTokenValue: string): boolean {
    const activeOrg = this.orgFilterList.find(
      org =>
        org.slug === filterTokenValue || org.id.toString() === filterTokenValue
    );

    return (
      !!activeOrg &&
      activeOrg.tenantId !== +this.sessionService.getTenantId() &&
      !this.sessionService.authenticated &&
      !this.centralConfig.innovationSpaceId
    );
  }

  handleFilterTokenFromOutside(): Observable<number> {
    return this.filterStoredService
      .getFilterTokenAddedFromOutside()
      .pipe(filter(Boolean), untilDestroyed(this));
  }

  getFilterTokenUrl(orgId: number): string {
    return (
      globalThis.origin +
      this.sessionService.appendLanguagePath(
        `${environment.jipUrl.organizations}/${orgId}`
      )
    );
  }

  addNewOrgFilterToken(newOrgId: number): void {
    const challengeService = this.injector.get(ChallengeHttpService);
    const orgService = this.injector.get(OrganizationHttpService);

    // Note: The backend has separate APIs for 'challenge' and 'organization'. Due to the
    // indistinguishable nature of entities (organization or challenge) before receiving the response,
    // a retry mechanism is implemented for both APIs.
    const handleReadError = (err: HttpErrorResponse) => {
      if (err.status === HttpStatusCodeEnum.NotFound) {
        return challengeService.read(newOrgId);
      }
      return throwError(() => new Error(err.message));
    };

    this.sessionService.apiReady$
      .pipe(untilDestroyed(this))
      .subscribe(apiReady => {
        if (apiReady) {
          orgService
            .read(newOrgId)
            .pipe(catchError(handleReadError), untilDestroyed(this))
            .subscribe({
              next: (org: OrganizationInterface) => {
                if (org) {
                  const orgObj: OrganizationFilter = {
                    id: org.id,
                    name: org.orgName,
                    imgUrl: org.logo?.url || org.logo,
                    isChallenge: org.showAsChallenge,
                    tenantId: +this.sessionService.getTenantId(),
                    slug: org.slug,
                  };

                  this.orgFilterList.push(orgObj);
                  this.orgFilterList$.next(this.orgFilterList);

                  this.filterOrganizations(orgObj.id, true).then(() => {
                    this.setFilterTokenStorage();
                  });
                }
              },
              error: err => {
                console.log(err);
              },
            });
        }
      });
  }

  updateOrgFilterTokenToLatest(orgId: number): void {
    this.sessionService.apiReady$
      .pipe(untilDestroyed(this))
      .subscribe(apiReady => {
        if (apiReady) {
          this.orgService
            .read(orgId)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: (org: OrganizationInterface) => {
                if (org) {
                  const orgObj: OrganizationFilter = {
                    id: org.id,
                    name: org.orgName,
                    imgUrl: org.logo?.url || org.logo,
                    isChallenge: org.showAsChallenge,
                    tenantId: +this.sessionService.getTenantId(),
                    slug: org.slug,
                  };

                  const updateOrgIndex = this.orgFilterList.findIndex(
                    obj => obj.id === orgId
                  );
                  if (updateOrgIndex !== null && updateOrgIndex !== undefined) {
                    this.orgFilterList[updateOrgIndex] = orgObj;
                    this.orgFilterList$.next(this.orgFilterList);

                    this.setFilterTokenStorage();
                  }
                }
              },
              error: err => {
                console.log(err);
              },
            });
        }
      });
  }

  async setFilterTokenStorage(): Promise<void> {
    await this.userConfig.set(
      StorageEnum.orgFilterToken,
      this.removeDuplicates(this.orgFilterList)
    );
  }

  onClickItem(id: string): void {
    document.getElementById(id).click();
    this.dropdown?.close();
  }

  ngOnDestroy(): void {
    this.eventBus.removeEventListener(this.globalPaneEvent);
  }

  positioningDropdown(event: boolean): void {
    if (!event || this.dropDownPlacement !== 'bottom') return;

    setTimeout(() => {
      const ele = document.getElementsByClassName('dropdown-menu show')[0];

      this.renderer.setStyle(ele, 'transform', this.dropDownOffsetX);
      this.renderer.setStyle(ele, '-webkit-transition', '0.1s ease-in-out');
      this.renderer.setStyle(ele, '-moz-transition', '0.1s ease-in-out');
      this.renderer.setStyle(ele, '-o-transition', '0.1s ease-in-out');
    });
  }

  checkDropDown(dropdown: NgbDropdown): void {
    this.dropdown = this.dropdowns.find(
      x => x['_elementRef'].nativeElement === dropdown
    );
  }

  selectAll(organizationId: number, event: Event): void {
    if (this.organizationId) {
      this.filterOrganizations(organizationId);
    } else {
      event.preventDefault();
    }
  }

  async removeItem(orgFilter: OrganizationFilter): Promise<void> {
    if (this.organizationId === orgFilter.id) {
      await this.filterOrganizations(orgFilter.id);
    }

    this.orgFilterList = this.orgFilterList.filter(
      org => org.id !== orgFilter.id
    );

    this.orgFilterList$.next(this.orgFilterList);

    this.setFilterTokenStorage().then();
  }

  private afterFilterSelection(filterCriteria: GlobalFilterCriteria): void {
    this.triggerGlobalFilterSelectionEvent(filterCriteria);
    this.triggerGlobalFilterEvent(filterCriteria);
  }

  protected listenFilterPaneEvent(): void {
    this.globalPaneEvent = this.eventBus.addEventListener({
      name: GlobalEvent.FromFilterPane,
      callback: async ({ filterPaneOnly, isFromFilterBtn }) => {
        const filterCriteria = await this.saveFilterCriteria();
        const subOrgFromUrlId = StringUtils.getParamFromUrl(
          UrlParam.SubOrganizationId
        );
        if (subOrgFromUrlId) {
          filterCriteria.threadId = +subOrgFromUrlId;
        }
        filterCriteria.keyword = this.searchKeyword;
        filterCriteria.fromFilterPane = true;
        this.triggerGlobalFilterEvent(
          filterCriteria,
          false,
          filterPaneOnly,
          isFromFilterBtn
        );
      },
    });
  }

  protected async updateFilterTokenStatus(orgId: number): Promise<void> {
    if (orgId > 0) {
      const orgExisted =
        (this.orgFilterList.length > 0 &&
          this.orgFilterList.map(x => x.id).includes(orgId)) ||
        (this.popularOrgs.length > 0 &&
          this.popularOrgs.map(x => x.id).includes(orgId));

      if (orgExisted) {
        if (this.organizationId !== orgId) {
          await this.filterOrganizations(orgId);
        }
      } else {
        this.addNewOrgFilterToken(orgId);
      }
    }
  }

  protected async saveFilterCriteria(): Promise<GlobalFilterCriteria> {
    return this.filterStoredService.saveFilterCriteria(
      this.searchKeyword,
      location.pathname,
      this.organizationId,
      this.threadId,
      this.threadEntity
    );
  }

  private triggerGlobalFilterEvent(
    filterCriteria: GlobalFilterCriteria,
    isManualSearch = false,
    filterPaneOnly = false,
    isFromFilterBtn = false
  ): void {
    this.filterStoredService.setCurrentFilterToken(
      filterCriteria.organizationIds[0]
    );

    const entityName = this.getEntitySearchTab();
    const searchResultsPath = environment.jipUrl.searchResults;

    if (
      !globalThis.location.href.includes(searchResultsPath) &&
      isManualSearch
    ) {
      localStorage.setItem(StorageEnum.isNewSearch, 'true');

      setTimeout(() => {
        window.open(
          StringUtils.propagateParamFromCurrentUrl(
            this.sessionService.appendLanguagePath(
              searchResultsPath + `/?${UrlParam.GlobalSearchTab}=${entityName}`
            ),
            UrlParam.FilterTokenId
          ),
          '_blank' // <- This is what makes it open in a new window.
        );
      });

      return;
    }

    this.eventBus.triggerEvent(GlobalEvent.Filter, {
      filterCriteria,
      filterPaneOnly,
      isFromFilterBtn,
    });
  }

  protected listenAddOrgToTokenEvent(): void {
    this.handleFilterTokenFromOutside()
      .pipe(untilDestroyed(this))
      .subscribe(async (orgId: number) => {
        await this.addFilterTokenFromOutside(orgId);
      });
  }

  protected async addFilterTokenFromOutside(orgId: number): Promise<void> {
    await this.updateFilterTokenStatus(orgId);
  }

  //#endregion End Handle Filter Token
  private triggerGlobalFilterSelectionEvent(
    filterCriteria: GlobalFilterCriteria
  ): void {
    this.eventBus.triggerEvent(GlobalEvent.FilterSelection, filterCriteria);
  }

  private initOrgService(): void {
    const isOrganization = this.window.location.pathname.includes(
      environment.jipUrl.organizations
    );
    const entityName = isOrganization
      ? EntityName.Organization
      : EntityName.Challenge;

    this.orgService = this.factoryService.createServiceByEntityName(entityName);
  }

  protected handleListenToTopicFilterChange() {
    this.filterStoredService
      .getTopicFilter()
      .pipe(untilDestroyed(this))
      .subscribe((res: { threadId: number; threadEntity: string }) => {
        this.threadId = res.threadId;
        this.threadEntity = res.threadEntity;
      });
  }

  private getEntitySearchTab(): string {
    const isOnHomePage = this.sessionService.isOnHomePage;
    const entityName = isOnHomePage
      ? EntityName.News
      : StringUtils.getEntityNameByUrl(globalThis.location.href);
    const currentSearchEntity = StringUtils.getParamFromUrl(
      UrlParam.GlobalSearchTab
    );

    return currentSearchEntity || entityName;
  }

  async handleSubOrgFromUrl(): Promise<void> {
    const subOrgFromUrlId = StringUtils.getParamFromUrl(
      UrlParam.SubOrganizationId
    );

    if (!subOrgFromUrlId && !this.sessionService.hasTopicFilter) {
      await this.searchChannel.resetChannel();
    }

    const currentSubOrg = await this.userConfig.get(
      StorageEnum.activeFilterChannel
    );

    if (subOrgFromUrlId || currentSubOrg) {
      const orgId = +subOrgFromUrlId || currentSubOrg.id;

      if (currentSubOrg?.entityName === EntityName.Event) return;

      this.orgHttpService
        .read(orgId)
        .pipe(untilDestroyed(this))
        .subscribe((org: OrganizationInterface) => {
          const activeOrg = {
            displayName: org.orgName,
            entityLogo: 'organisation',
            entityName: EntityName.Organization,
            id: org.id,
            image: org.logo,
          };

          this.searchChannel.onSelectChannel(activeOrg);

          if (this.sessionService.hasTopicFilter) {
            this.searchChannel.activeFilter$.next(activeOrg);
          }
        });
    }
  }

  shouldShowFilterToken(token: OrganizationFilter): boolean {
    if (this.sessionService.authenticated) return true;

    return token.tenantId === +this.sessionService.getTenantId();
  }

  navigateToActiveInnovationSpaceDetail(id = null) {
    if (id) {
      FormUtils.navigateToWithLanguagePath(
        this.sessionService,
        this.orgUrl + '/' + id
      );
    } else {
      FormUtils.navigateToWithLanguagePath(this.sessionService, '/');
    }
  }

  toggleSearch() {
    this.isShowSearch = !this.isShowSearch;
  }

  removeDuplicates(arr: OrganizationFilter[]): OrganizationFilter[] {
    return arr.filter((item, index, self) => {
      return self.findIndex(t => t.id === item.id) === index;
    });
  }
}
